/* eslint-disable camelcase */
import axios, {
    AxiosInstance,
    AxiosResponse,
    AxiosRequestConfig,
    AxiosError,
} from 'axios';
import _ from 'lodash';
import { notification } from 'antd';

import { createError } from './createError';
import {
    camelizeKeys,
    downloadResponse,
    renderUrl,
    blobToObject,
    Stringifiable,
} from '../lib';
import { API_BASE_URL } from '../config';
import {
    getBypassToken,
    getRecaptchaToken,
    getRecaptchav2Token,
    saveBypassToken,
} from '../lib/googleRecaptcha';
import { RecaptchaActions, RECAPTCHA_ERROR_CODE } from '../constants/recaptcha';
import { getWorkspaceAliasFromUrl } from '../lib/workspace';
import { errors } from './errors';
import { InternalServerError } from './errors/base';
import { DateTime } from 'luxon';
import { shouldSkipRecaptcha } from '../lib/api';

export type ApiRequestConfig = AxiosRequestConfig & {
    __retriesOnTimeoutCount?: number;
    skipCamelization?: boolean;
    action?: RecaptchaActions;
};

type TweakedAxiosClient = AxiosInstance & {
    __request: AxiosInstance['request'];
    buildUrl(path: string, params?: Record<string, unknown>): string;
};

type ApiClient = Omit<TweakedAxiosClient, 'request'> & {
    request<T>(
        config: ApiRequestConfig,
        recaptchaAction?: RecaptchaActions
    ): Promise<T>;
};

type RawApiClientConfig = Pick<
    ApiRequestConfig,
    'url' | 'method' | 'params' | 'paramsSerializer'
> & {
    fallbackName?: string;
    action?: RecaptchaActions;
};

type RawApiClient = Omit<AxiosInstance, 'request'> & {
    download(config: RawApiClientConfig): Promise<void>;
};

type ApiResponse<T = unknown> = {
    status: 'ok' | 'error';
    customCode?: number;
    message?: string;
    result: T;
    bypassRecaptchaTokenV2?: string;
};

const withRecaptchaTokensAndWorkspace = async (
    config: ApiRequestConfig,
    recaptchaVersion: 'v2' | 'v3'
) => {
    if (!config.action || shouldSkipRecaptcha(config.action)) {
        return {
            ...config,
            params: {
                workspace_alias: getWorkspaceAliasFromUrl(),
                ...config.params,
            },
        };
    }
    return {
        ...config,
        params: {
            workspace_alias: getWorkspaceAliasFromUrl(),
            ...config.params,
            recaptcha_token: await getRecaptchaToken(config.action),
            current_time: DateTime.now().toMillis(),
            recaptcha_token_v2:
                recaptchaVersion === 'v2' ? await getRecaptchav2Token() : null,
            bypass_recaptcha_token_v2: getBypassToken(),
        },
    };
};

const MAX_TIMEOUT_RETRIES = 3;

const clientConfig = {
    baseURL: API_BASE_URL,
    timeout: 30000,
    validateStatus: () => true,
    withCredentials: true,
};

const rawClient = axios.create(clientConfig) as unknown as RawApiClient;

rawClient.download = async ({
    fallbackName = 'file',
    url,
    method = 'GET',
    action,
    params,
    paramsSerializer,
}: RawApiClientConfig) => {
    await (rawClient as unknown as AxiosInstance)
        .request(
            await withRecaptchaTokensAndWorkspace(
                {
                    method,
                    url,
                    responseType: 'blob',
                    action,
                    params: {
                        workspace_alias: getWorkspaceAliasFromUrl(),
                        ...params,
                    },
                    paramsSerializer,
                },
                'v3'
            )
        )
        .then(response => {
            if (response.status >= 400) {
                return blobToObject(response).then(backendErrorHandler);
            }
            return downloadResponse(response, fallbackName);
        });
};

const __client = axios.create(clientConfig) as TweakedAxiosClient;

__client.__request = __client.request;

const request = async <T>(config: ApiRequestConfig): Promise<T> => {
    return __client
        .__request<ApiResponse<T>>(
            await withRecaptchaTokensAndWorkspace(config, 'v3')
        )
        .then(response => response.data.result);
};

__client.request = request;
__client.buildUrl = (
    path: string,
    params?: Record<string, Stringifiable>
): string => {
    return renderUrl(client.defaults.baseURL + path, undefined, params);
};
const client = __client as ApiClient;

const isReadOp = (method: ApiRequestConfig['method'] = 'get') =>
    !['delete', 'post', 'put', 'patch'].includes(method.toLowerCase());

const canRetry = (nextRetryCount = 0) => nextRetryCount < MAX_TIMEOUT_RETRIES;

const getNextRetryCount = (currentCount = -1) => currentCount + 1;

const isTimeout = (error: AxiosError) =>
    error.isAxiosError && error.message.startsWith('timeout of');

const notifyTimeoutAndForward = (
    error: AxiosError,
    action: 'read' | 'write'
) => {
    const actionMessage =
        action === 'read'
            ? ''
            : ' Tente reiniciar a página e conferir se a ação foi bem sucedida.';

    notification.error({
        duration: 0,
        message: `O sistema está demorando a responder.${actionMessage} Nosso time de
            tecnologia foi notificado e vamos averiguar a questão.`,
    });

    return Promise.reject(error);
};

const retryOnTimeoutError = (error: AxiosError) => {
    if (isTimeout(error)) {
        const config = error.config as ApiRequestConfig;

        if (!isReadOp(config.method))
            return notifyTimeoutAndForward(error, 'write');

        config.__retriesOnTimeoutCount = getNextRetryCount(
            config.__retriesOnTimeoutCount
        );

        return canRetry(config.__retriesOnTimeoutCount)
            ? client.__request(config)
            : notifyTimeoutAndForward(error, 'read');
    }

    return Promise.reject(error);
};

const backendErrorHandler = (response: AxiosResponse) => {
    if (response.data.status === 'error') {
        const error = createError({
            message: response.data.message,
            code: response.data.customCode,
            url: response.config.url,
            response: response.data.result,
        });
        if (error instanceof errors.RateLimitExceeded) {
            notification.error({
                message: 'Aguarde alguns segundos para tentar novamente.',
            });
        }
        if (error instanceof errors.AdminCantAccessNonReadOnlyResource) {
            notification.error({
                message: 'Você não pode realizar essa ação em nome do usuário.',
            });
        }
        return Promise.reject(error);
    }
    if (response.status === 500) {
        notification.error({
            message: `Ocorreu um erro inesperado. Por favor, aguarde alguns
            instantes para tentar novamente ou contate o suporte.`,
            key: 'InternalServerError',
        });
        return Promise.reject(
            new InternalServerError(
                response.data,
                response.status,
                response.config.url,
                response.statusText
            )
        );
    }
    return Promise.reject(response);
};

const parseResponse = (response: AxiosResponse<ApiResponse>) => {
    if (response.data.status === 'ok') {
        return response;
    }
    return backendErrorHandler(response);
};

const camelizeResponse = (response: AxiosResponse<unknown>) => {
    return { ...response, data: camelizeKeys(response.data) };
};

client.interceptors.response.use(response => {
    if (response.data.custom_code === RECAPTCHA_ERROR_CODE) {
        return withRecaptchaTokensAndWorkspace(response.config, 'v2')
            .then(__client.__request)
            .then(saveBypassToken);
    }
    return Promise.resolve(response);
});

client.interceptors.response.use(response => {
    const config = response.config as ApiRequestConfig;
    return config.skipCamelization ? response : camelizeResponse(response);
});

client.interceptors.response.use(parseResponse);
client.interceptors.response.use(_.identity, retryOnTimeoutError);

export default client;
export { MAX_TIMEOUT_RETRIES, rawClient };
export type { ApiResponse };
